import type { HttpClient } from '@shared/http/http-client';
import type { IScanConnectionDetailRepository } from '@domain/data-contracts/scan-connection-detail-repository.interface';
import type {
  ScanConnectionDetail,
  ScanConnectionCreateRequest,
  ClientMappingCreateRequest,
  ClientMapping,
} from '@domain/models/scan-connection.interface';
import { ScanConnectionStatus } from '@domain/models/scan-connection.interface';

interface ClientMappingDto {
  readonly mappingId: number;
  readonly clientId: number;
  readonly clientName: string;
  readonly externalClientId: string;
}

interface ScanConnectionDetailDto {
  readonly connectionId: number;
  readonly organizationId: number;
  readonly displayName: string;
  readonly provider: string;
  readonly method: string;
  readonly status: string;
  readonly clientMappings: readonly ClientMappingDto[];
}

function coerceScanConnectionStatus(raw: string): ScanConnectionStatus {
  if (raw === ScanConnectionStatus.Active) { return ScanConnectionStatus.Active; }
  return ScanConnectionStatus.Inactive;
}

function mapClientMappingDto(dto: ClientMappingDto): ClientMapping {
  return {
    mappingId: dto.mappingId,
    clientId: dto.clientId,
    clientName: dto.clientName,
    externalClientId: dto.externalClientId,
  };
}

function mapScanConnectionDetailDto(dto: ScanConnectionDetailDto): ScanConnectionDetail {
  return {
    connectionId: dto.connectionId,
    organizationId: dto.organizationId,
    displayName: dto.displayName,
    provider: dto.provider,
    method: dto.method,
    status: coerceScanConnectionStatus(dto.status),
    clientMappings: dto.clientMappings.map(mapClientMappingDto),
  };
}

export class ScanConnectionDetailRepository implements IScanConnectionDetailRepository {
  public constructor(private readonly http: HttpClient) {}

  public async listForOrg(
    orgId: number,
    signal?: AbortSignal,
  ): Promise<readonly ScanConnectionDetail[]> {
    const response = await this.http.get<readonly ScanConnectionDetailDto[]>(
      `/api/organizations/${orgId}/connections`,
      { signal },
    );
    return response.data.map(mapScanConnectionDetailDto);
  }

  public async getById(
    orgId: number,
    connectionId: number,
    signal?: AbortSignal,
  ): Promise<ScanConnectionDetail> {
    const response = await this.http.get<ScanConnectionDetailDto>(
      `/api/organizations/${orgId}/connections/${connectionId}`,
      { signal },
    );
    return mapScanConnectionDetailDto(response.data);
  }

  public async create(
    req: ScanConnectionCreateRequest,
    signal?: AbortSignal,
  ): Promise<ScanConnectionDetail> {
    const response = await this.http.post<ScanConnectionDetailDto>(
      `/api/organizations/${req.organizationId}/connections`,
      req,
      { signal },
    );
    return mapScanConnectionDetailDto(response.data);
  }

  public async addMapping(
    orgId: number,
    connectionId: number,
    req: ClientMappingCreateRequest,
    signal?: AbortSignal,
  ): Promise<void> {
    await this.http.post(
      `/api/organizations/${orgId}/connections/${connectionId}/mappings`,
      req,
      { signal },
    );
  }
}
